import React, { useState } from 'react';
import { Stats } from '../types';

interface StatAllocationModalProps {
  isOpen: boolean;
  availablePoints: number;
  currentStats: Stats;
  onAllocate: (statAllocations: Partial<Stats>) => void;
  onClose: () => void;
}

interface BuildPreset {
  name: string;
  description: string;
  icon: string;
  allocation: Partial<Stats>;
}

const BUILD_PRESETS: BuildPreset[] = [
  {
    name: 'Tank',
    description: 'High vitality and strength for survivability',
    icon: '🛡️',
    allocation: { vitality: 2, strength: 1 }
  },
  {
    name: 'DPS',
    description: 'Maximize damage with strength and agility',
    icon: '⚔️',
    allocation: { strength: 2, agility: 1 }
  },
  {
    name: 'Mage',
    description: 'Focus on intelligence for magical power',
    icon: '🔮',
    allocation: { intelligence: 2, sense: 1 }
  },
  {
    name: 'Assassin',
    description: 'Speed and precision with agility and sense',
    icon: '🗡️',
    allocation: { agility: 2, sense: 1 }
  },
  {
    name: 'Balanced',
    description: 'Well-rounded distribution across all stats',
    icon: '⚖️',
    allocation: { strength: 1, intelligence: 1, agility: 1, vitality: 1, sense: 1 }
  },
];

const StatAllocationModal: React.FC<StatAllocationModalProps> = ({
  isOpen,
  availablePoints,
  currentStats,
  onAllocate,
  onClose,
}) => {
  const [allocations, setAllocations] = useState<Partial<Stats>>({
    strength: 0,
    intelligence: 0,
    agility: 0,
    vitality: 0,
    sense: 0,
  });

  if (!isOpen) return null;

  const totalAllocated = Object.values(allocations).reduce((sum, val) => sum + val, 0);
  const remainingPoints = availablePoints - totalAllocated;

  const handleIncrease = (stat: keyof Stats) => {
    if (remainingPoints > 0) {
      setAllocations(prev => ({
        ...prev,
        [stat]: (prev[stat] || 0) + 1,
      }));
    }
  };

  const handleDecrease = (stat: keyof Stats) => {
    if ((allocations[stat] || 0) > 0) {
      setAllocations(prev => ({
        ...prev,
        [stat]: (prev[stat] || 0) - 1,
      }));
    }
  };

  const handlePresetApply = (preset: BuildPreset) => {
    if (availablePoints === 0) return;

    // Special handling for balanced preset - ensure true balance across all 5 stats
    if (preset.name === 'Balanced') {
      const pointsPerStat = Math.floor(availablePoints / 5);
      const remainder = availablePoints % 5;

      const newAllocations: Partial<Stats> = {
        strength: pointsPerStat,
        intelligence: pointsPerStat,
        agility: pointsPerStat,
        vitality: pointsPerStat,
        sense: pointsPerStat,
      };

      // Distribute remainder points
      const stats: (keyof Stats)[] = ['strength', 'intelligence', 'agility', 'vitality', 'sense'];
      for (let i = 0; i < remainder; i++) {
        newAllocations[stats[i]] = (newAllocations[stats[i]] || 0) + 1;
      }

      setAllocations(newAllocations);
      return;
    }

    // Simple allocation for other presets
    const presetTotal = Object.values(preset.allocation).reduce((sum, val) => sum + val, 0);
    const fullCycles = Math.floor(availablePoints / presetTotal);
    const remainingPoints = availablePoints % presetTotal;

    const newAllocations: Partial<Stats> = {
      strength: 0,
      intelligence: 0,
      agility: 0,
      vitality: 0,
      sense: 0,
    };

    // Apply full cycles
    Object.entries(preset.allocation).forEach(([stat, value]) => {
      newAllocations[stat as keyof Stats] = value * fullCycles;
    });

    // Distribute remaining points to stats with highest values in preset
    if (remainingPoints > 0) {
      const sortedStats = Object.entries(preset.allocation)
        .sort(([, a], [, b]) => b - a)
        .map(([stat]) => stat as keyof Stats);

      for (let i = 0; i < remainingPoints; i++) {
        const stat = sortedStats[i % sortedStats.length];
        newAllocations[stat] = (newAllocations[stat] || 0) + 1;
      }
    }

    setAllocations(newAllocations);
  };

  const handleConfirm = () => {
    if (totalAllocated > 0) {
      onAllocate(allocations);
      setAllocations({
        strength: 0,
        intelligence: 0,
        agility: 0,
        vitality: 0,
        sense: 0,
      });
    }
  };

  const handleReset = () => {
    setAllocations({
      strength: 0,
      intelligence: 0,
      agility: 0,
      vitality: 0,
      sense: 0,
    });
  };

  const statConfig = [
    { key: 'strength' as keyof Stats, label: 'Strength', iconPath: '/icons/icon-stat-strength.png', color: 'red' },
    { key: 'intelligence' as keyof Stats, label: 'Intelligence', iconPath: '/icons/icon-stat-intelligence.png', color: 'blue' },
    { key: 'agility' as keyof Stats, label: 'Agility', iconPath: '/icons/icon-stat-agility.png', color: 'yellow' },
    { key: 'vitality' as keyof Stats, label: 'Vitality', iconPath: '/icons/icon-stat-vitality.png', color: 'green' },
    { key: 'sense' as keyof Stats, label: 'Sense', iconPath: '/icons/icon-stat-sense.png', color: 'purple' },
  ];

  return (
    <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center z-[9998] p-4 modal-backdrop animate-fade-in">
      <div className="relative max-w-xl w-full animate-scale-in">
        {/* Main Card */}
        <div className="bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl shadow-2xl overflow-hidden max-h-[82vh] overflow-y-auto"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >

          {/* Content */}
          <div className="relative p-4 sm:p-5">
            {/* Header */}
            <div className="text-center mb-3">
              <h2 className="text-base sm:text-xl font-orbitron font-black mb-1 tracking-wider uppercase" style={{ color: 'var(--color-primary)' }}>
                Stat Allocation
              </h2>
              <p className="text-gray-300 font-rajdhani text-xs">
                Allocate your stat points to customize your build
              </p>
              <div className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-b from-blue-800/40 via-blue-900/30 to-blue-950/40 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 rounded-2xl"
                style={{
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.15)'
                }}
              >
                <img 
                  src="/icons/icon-statpoints.png" 
                  alt="Stat Points"
                  className="w-6 h-6 object-contain"
                />
                <span className="text-2xl font-black font-orbitron" style={{ color: 'var(--color-primary)' }}>
                  {remainingPoints}
                </span>
                <span className="text-xs text-gray-400 font-rajdhani ml-2">Points Remaining</span>
              </div>
            </div>

            {/* Build Presets */}
            <div className="mb-4">
              <h3 className="text-xs font-orbitron font-bold text-gray-400 uppercase mb-2 tracking-wider">
                Quick Build Presets
              </h3>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {BUILD_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => handlePresetApply(preset)}
                    className="bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl p-2 transition-all group hover:border-blue-500/50 hover:scale-105 relative"
                    title={preset.description}
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/10 rounded-xl"></div>
                    <div className="relative z-10 text-xl mb-0.5">{preset.icon}</div>
                    <div className="relative z-10 text-[9px] font-orbitron font-bold text-white group-hover:text-blue-400">
                      {preset.name}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Stat Allocation */}
            <div className="space-y-2 mb-4">
              {statConfig.map((stat) => {
                const currentValue = currentStats[stat.key];
                const allocatedValue = allocations[stat.key] || 0;
                const newValue = currentValue + allocatedValue;

                return (
                  <div
                    key={stat.key}
                    className="bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl p-2.5"
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <div className="flex items-center justify-between">
                      {/* Stat Info */}
                      <div className="flex items-center gap-2 flex-1">
                        <img 
                          src={stat.iconPath} 
                          alt={stat.label}
                          className="w-6 h-6 object-contain"
                        />
                        <div>
                          <div className="font-orbitron font-bold text-white text-[10px] uppercase">
                            {stat.label}
                          </div>
                          <div className="text-[9px] text-gray-400 font-rajdhani">
                            {currentValue} → {newValue}
                            {allocatedValue > 0 && (
                              <span className="text-green-400 ml-1">(+{allocatedValue})</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Allocation Controls */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDecrease(stat.key)}
                          disabled={allocatedValue === 0}
                          className="w-8 h-8 bg-gradient-to-b from-red-800/50 via-red-900/40 to-red-950/50 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-700/70 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg font-bold text-red-400 transition-all text-base hover:scale-110 relative"
                          style={{
                            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                          }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-lg"></div>
                          <span className="relative z-10">−</span>
                        </button>
                        <div className="w-10 text-center font-orbitron font-bold text-lg bg-gradient-to-b from-slate-800/40 via-slate-900/30 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-lg py-1.5 px-2 text-blue-500"
                          style={{
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                          }}
                        >
                          {allocatedValue}
                        </div>
                        <button
                          onClick={() => handleIncrease(stat.key)}
                          disabled={remainingPoints === 0}
                          className="w-8 h-8 bg-gradient-to-b from-green-800/50 via-green-900/40 to-green-950/50 backdrop-blur-sm border-t-2 border-l-2 border-green-500/50 border-r-2 border-b-2 border-r-green-700/70 border-b-green-700/70 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg font-bold text-green-400 transition-all text-base hover:scale-110 relative"
                          style={{
                            boxShadow: '0 2px 8px rgba(34, 197, 94, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                          }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-lg"></div>
                          <span className="relative z-10">+</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleReset}
                disabled={totalAllocated === 0}
                className="flex-1 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 font-orbitron font-bold py-2 px-3 uppercase tracking-wider transition-all text-xs hover:scale-105 relative"
                style={{
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/10 rounded-xl"></div>
                <span className="relative z-10">Reset</span>
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gradient-to-b from-red-800/40 via-red-900/40 to-red-950/40 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-700/70 text-red-400 font-orbitron font-bold py-2 px-3 rounded-xl uppercase tracking-wider transition-all text-xs hover:scale-105 relative"
                style={{
                  boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl"></div>
                <span className="relative z-10">Cancel</span>
              </button>
              <button
                onClick={handleConfirm}
                disabled={totalAllocated === 0}
                className="flex-1 bg-gradient-to-b from-blue-800/60 via-purple-800/50 to-blue-950/60 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-purple-700/70 border-b-purple-700/70 disabled:opacity-30 disabled:cursor-not-allowed text-white font-orbitron font-bold py-2 px-3 rounded-xl uppercase tracking-wider transition-all text-xs hover:scale-105 relative"
                style={{
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl"></div>
                <span className="relative z-10">Confirm ({totalAllocated})</span>
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default StatAllocationModal;

